/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isBlank, isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import RouterService from '@ember/routing/router-service';
import AuthService from 'cio/services/auth';

interface CioConditionEditorTypeSelectArgs {
  disabled: boolean;
  actionItems?: ITEM[];
  customerItems?: ITEM[];
  groupItems?: ITEM[];
  conditionType: string;
  resetValueAfterSelection?: boolean;
  changeType: (type: string) => unknown;
}

type ITEM = {
  value: string;
  label: string;
};

export default class CioConditionEditorTypeSelect extends Component<CioConditionEditorTypeSelectArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service router!: RouterService;

  select: HTMLSelectElement | null = null;

  get hidden() {
    return isEmpty(this.args.actionItems) && isEmpty(this.args.customerItems) && isEmpty(this.args.groupItems);
  }

  @action
  handleSelectInsert(el: HTMLSelectElement) {
    this.select = el;
  }

  @action
  changeTypeAction(event: Event & { target: HTMLSelectElement }) {
    let { value } = event.target;
    this.args.changeType(value);
    this.trackConditionSelected(value);

    // In some scenarios (e.g. adding new condition using this select, at bottom of conditions group) we need to reset its value back to the default.
    // The select doesn't rerender (which is fine), so the selected option stays selected, which is something we don't want.
    if (this.args.resetValueAfterSelection && this.select != null) {
      this.select.value = '';
    }
  }

  private trackConditionSelected(value: string) {
    if (isBlank(value)) {
      return;
    }

    let getValueLabel = (items: ITEM[] | undefined) => (items ?? []).findBy('value', value)?.label;
    let selected = [
      { items: this.args.actionItems, group: 'Message Data' },
      { items: this.args.customerItems, group: 'User Data' },
      { items: this.args.groupItems, group: 'Group' },
    ].reduce(
      (previous, current) =>
        !previous.conditionValue
          ? { conditionValue: getValueLabel(current.items), conditionType: current.group }
          : previous,
      { conditionValue: null, conditionType: null },
    );
    let { conditionType, conditionValue } = selected;

    if (conditionType !== null && conditionValue !== null) {
      this.analytics.track('segment', 'segment_condition_selected', {
        conditionType,
        conditionValue,
        route: this.router.currentRoute,
      });
    }
  }

  @action
  resetSelectedIndex() {
    if (this.select == null) {
      return;
    }

    let selectedOptionIndex = Array.from(this.select.options).findIndex(
      (option) => option.value === this.args.conditionType,
    );

    if (selectedOptionIndex === -1) {
      return;
    }

    this.select.selectedIndex = selectedOptionIndex;
  }
}
